/**
 * SITREP controls (SITREP.1.5) — the operator entry point for generating a
 * Situation Report + a recent-SITREPs list.
 *
 * Rendered as a floating top-centre toolbar over the COP. The operator picks a
 * scope — "Current map view" (the live bbox) or "Selected incident" (only enabled
 * when a fire / high-risk hotspot is selected) — and clicks Generate; the report
 * opens in the SitrepDetailPanel modal. A collapsible "Recent" list reopens the
 * last few SITREPs (global, not session-scoped — #142). The server logs `generate_sitrep` (it owns
 * the new row id); the modal logs view/export/forward.
 */
import { useCallback, useEffect, useState } from 'react';
import type { SitrepListItem, SitrepReport } from '@tsr/shared-types';
import { createSitrep, fetchSitrep, fetchSitreps, type BboxQuery } from './api';
import { getSessionId } from './lib/operatorActions';
import { showToast } from './lib/toast';
import type { SavedRegion } from './savedViews';
import { showBarCaptions } from './lib/commandBarLayout';
import { useCommandBarTier } from './lib/commandBarTier';

export interface SitrepIncidentSelection {
  id: string;
  type: 'active_fire' | 'high_risk_hotspot';
  /** Short label for the scope dropdown ("Selected fire" / "Selected hotspot"). */
  label: string;
}

/** 'region' = the live map view; 'incident' = the selected fire/hotspot; a
 *  `saved:<id>` value scopes the report to a saved region's bbox (#154). */
type ScopeChoice = 'region' | 'incident' | `saved:${string}`;

export function SitrepControls({
  currentBbox,
  selectedIncident,
  savedRegions = [],
  onOpen,
}: {
  currentBbox: BboxQuery | null;
  selectedIncident: SitrepIncidentSelection | null;
  savedRegions?: SavedRegion[];
  onOpen: (sitrep: SitrepReport) => void;
}): JSX.Element {
  const captions = showBarCaptions(useCommandBarTier());
  const [scope, setScope] = useState<ScopeChoice>('region');
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState<SitrepListItem[]>([]);
  const [showRecent, setShowRecent] = useState(false);

  // Recent list is GLOBAL (no session_id) so an oncoming operator sees the prior
  // operator's reports across a shift handover (#142). getSessionId() still scopes
  // the SITREPs this operator *generates* below.
  const refreshRecent = useCallback(() => {
    void fetchSitreps(undefined, 5)
      .then((r) => setRecent(r.sitreps))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    refreshRecent();
  }, [refreshRecent]);

  // If the selected incident disappears, fall back to the region scope.
  useEffect(() => {
    if (scope === 'incident' && !selectedIncident) setScope('region');
  }, [scope, selectedIncident]);

  // If the selected saved region is deleted, fall back to the live map view.
  useEffect(() => {
    if (scope.startsWith('saved:')) {
      const id = scope.slice('saved:'.length);
      if (!savedRegions.some((r) => r.id === id)) setScope('region');
    }
  }, [scope, savedRegions]);

  const generate = (): void => {
    if (busy) return;
    const savedRegion = scope.startsWith('saved:')
      ? savedRegions.find((r) => r.id === scope.slice('saved:'.length))
      : undefined;
    const body =
      scope === 'incident' && selectedIncident
        ? ({
            scope_type: 'incident' as const,
            scope_payload: {
              incident_id: selectedIncident.id,
              incident_type: selectedIncident.type,
            },
            hazard_focus: 'wildfire' as const,
            session_id: getSessionId(),
          })
        : savedRegion
          ? ({
              scope_type: 'region' as const,
              scope_payload: savedRegion.bbox,
              hazard_focus: 'wildfire' as const,
              session_id: getSessionId(),
            })
          : currentBbox
            ? ({
                scope_type: 'region' as const,
                scope_payload: currentBbox,
                hazard_focus: 'wildfire' as const,
                session_id: getSessionId(),
              })
            : null;
    if (!body) {
      showToast('Map view not ready yet — pan or zoom once, then retry.', { variant: 'error' });
      return;
    }
    setBusy(true);
    void createSitrep(body)
      .then((sitrep) => {
        onOpen(sitrep);
        refreshRecent();
        showToast(
          `SITREP generated — ${sitrep.active_fires_count} fires, ${sitrep.high_risk_hotspots_count} hotspots.`,
          { variant: 'success' },
        );
      })
      .catch(() => showToast('Could not generate the SITREP. Please retry.', { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  const reopen = (id: string): void => {
    void fetchSitrep(id)
      .then((sitrep) => onOpen(sitrep))
      .catch(() => showToast('Could not load that SITREP.', { variant: 'error' }));
  };

  return (
    <div
      data-testid="sitrep-controls"
      className="relative flex shrink-0 items-center gap-2 whitespace-nowrap"
    >
      {captions && (
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Report
        </span>
      )}
        <select
          data-testid="sitrep-scope-select"
          value={scope}
          onChange={(e) => setScope(e.target.value as ScopeChoice)}
          className="text-xs bg-slate-800 border border-slate-600 rounded px-1.5 py-1 text-slate-100"
        >
          <option value="region">Current map view</option>
          <option value="incident" disabled={!selectedIncident}>
            {selectedIncident ? selectedIncident.label : 'Selected incident'}
          </option>
          {savedRegions.length > 0 && (
            <optgroup label="Saved regions">
              {savedRegions.map((r) => (
                <option key={r.id} value={`saved:${r.id}`}>
                  {r.label}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        <button
          type="button"
          data-testid="generate-sitrep-btn"
          disabled={busy}
          onClick={generate}
          className="text-xs py-1 px-3 rounded bg-emerald-600/90 hover:bg-emerald-600 border border-emerald-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
        >
          {busy ? 'Generating…' : 'Generate'}
        </button>
        <button
          type="button"
          data-testid="sitrep-recent-toggle"
          onClick={() => {
            setShowRecent((v) => !v);
            if (!showRecent) refreshRecent();
          }}
          className="text-xs py-1 px-2 rounded bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-300"
        >
          Recent {recent.length > 0 ? `(${recent.length})` : ''} ▾
        </button>

      {showRecent && (
        <div
          data-testid="sitrep-recent-list"
          className="absolute left-0 top-full mt-1 z-50 min-w-[18rem] rounded-lg border border-slate-700 bg-slate-900/95 px-2 py-1.5 shadow-lg max-h-64 overflow-y-auto"
        >
          {recent.length === 0 ? (
            <p className="text-[11px] text-slate-500 px-1 py-1">No SITREPs yet.</p>
          ) : (
            <ul className="space-y-0.5">
              {recent.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    data-testid="sitrep-recent-item"
                    onClick={() => reopen(s.id)}
                    className="w-full text-left text-[11px] px-1.5 py-1 rounded hover:bg-slate-800 text-slate-300"
                  >
                    <span className="text-slate-200">{s.title}</span>
                    <span className="text-slate-500">
                      {' · '}
                      {s.created_at.slice(0, 16).replace('T', ' ')}Z
                      {/* #613 — response_level_max is the machine Threat roll-up (not an IC §5
                          assessment); the compact "L{n}" carries an explicit Threat-axis tooltip. */}
                      {s.response_level_max ? (
                        <span title={`Threat Level ${s.response_level_max}`}>{` · L${s.response_level_max}`}</span>
                      ) : (
                        ''
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
